import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import SwipeableDrawer from "@material-ui/core/SwipeableDrawer";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";

import isNativeApp from "../../utils/isNativeApp";

const useStyles = makeStyles((theme) => ({
  screenshot: {
    maxWidth: 280,
    display: "block",
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
  selector: {
    fontFamily: "monospace",
    fontSize: 11,
    color: theme.palette.text.secondary,
    backgroundColor:
      theme.palette.type === "dark" ? "#1e1e1e" : "#f5f5f5",
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    wordBreak: "break-all",
    display: "block",
  },
  drawerContent: {
    padding: theme.spacing(2),
  },
  drawerActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2, 2),
  },
  submitWrapper: {
    position: "relative",
    display: "inline-flex",
  },
  progress: {
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const MIN_DESC_LENGTH = 5;

const ContentBody = ({ screenshot, selector, description, setDescription, classes }) => (
  <>
    {screenshot && (
      <img src={screenshot} alt="screenshot" className={classes.screenshot} />
    )}
    {selector && (
      <Typography component="code" className={classes.selector}>
        {selector}
      </Typography>
    )}
    <TextField
      label="תיאור התקלה"
      multiline
      rows={3}
      fullWidth
      required
      variant="outlined"
      value={description}
      onChange={(e) => setDescription(e.target.value)}
      inputProps={{ dir: "rtl" }}
    />
  </>
);

const BugDialog = ({ open, onClose, screenshot, selector, onSubmit, loading }) => {
  const classes = useStyles();
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (!open) setDescription("");
  }, [open]);

  // Marking is mandatory: a report must reference a marked element. The widget
  // only opens this dialog after a successful element selection, but guard the
  // submit button explicitly too so a report can never be sent without a selector.
  const isSubmitDisabled =
    loading || description.trim().length < MIN_DESC_LENGTH || !selector;

  const handleSubmit = () => {
    if (!isSubmitDisabled) {
      onSubmit(description.trim());
    }
  };

  const actions = (
    <>
      <Button onClick={onClose} disabled={loading}>
        ביטול
      </Button>
      <Box className={classes.submitWrapper}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
        >
          שלח דוח
        </Button>
        {loading && (
          <CircularProgress size={24} className={classes.progress} />
        )}
      </Box>
    </>
  );

  if (isNativeApp()) {
    return (
      <SwipeableDrawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        onOpen={() => {}}
        disableSwipeToOpen
      >
        <Box className={classes.drawerContent}>
          <Typography variant="h6" gutterBottom>
            דווח על תקלה
          </Typography>
          <ContentBody
            screenshot={screenshot}
            selector={selector}
            description={description}
            setDescription={setDescription}
            classes={classes}
          />
        </Box>
        <Box className={classes.drawerActions}>{actions}</Box>
      </SwipeableDrawer>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>דווח על תקלה</DialogTitle>
      <DialogContent>
        <ContentBody
          screenshot={screenshot}
          selector={selector}
          description={description}
          setDescription={setDescription}
          classes={classes}
        />
      </DialogContent>
      <DialogActions>{actions}</DialogActions>
    </Dialog>
  );
};

export default BugDialog;
